import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BookingPage from "../../../subscreen/doctor/docbuttons/BookingPage";
import DoctorProfile from "../../../subscreen/doctor/docbuttons/DoctorProfile";
import { AppContext } from "../../../context/AppContext";
import "../../../assets/css/patient/Doctor.css";

const DoctorsSection = () => {
  const navigate    = useNavigate();
  const { baseUrl } = useContext(AppContext);
  const cardsRef    = useRef([]);

  const [doctors, setDoctors]           = useState([]);   // data from API
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBooking, setShowBooking]   = useState(false);
  const [showProfile, setShowProfile]   = useState(false);

  // ── Fetch approved doctors from API ──────────────────────────
  useEffect(() => {
    axios
      .get(`${baseUrl}/doctors`)   // Laravel: GET /api/doctors (approved only)
      .then((res) => {
        // Expected fields from backend:
        // id, name, specialization, education, experience,
        // location, fee, available, profile_image_url, rating, reviews
        setDoctors(res.data);
      })
      .catch((err) => {
        console.error("Doctors load error:", err);
        setError("Failed to load doctors, please refresh.");
      })
      .finally(() => setLoading(false));
  }, [baseUrl]);
  const handleBook = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBooking(true);
    setShowProfile(false);
  };

  const handleProfile = (doctor) => {
    setSelectedDoctor(doctor);
    setShowProfile(true);
    setShowBooking(false);
  };

  const handleClose = () => {
    setShowBooking(false);
    setShowProfile(false);
    setSelectedDoctor(null);
  };

  useEffect(() => {
    if (!showBooking && !showProfile) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [showBooking, showProfile]);

  return (
    <>
      {/* ===== HEADER ===== */}
      <section className="doctors-header-wrapper">
        <div className="doctors-header-particles">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="doctors-header-particle"></span>
          ))}
        </div>
        <div className="doctors-header-shape doctors-header-shape-1"></div>
        <div className="doctors-header-shape doctors-header-shape-2"></div>
        <div className="doctors-header-shape doctors-header-shape-3"></div>
        <div className="doctors-header-content">
          <div className="doctors-header-badge">
            <span className="doctors-header-dot"></span>
            Our Medical Experts
          </div>
          <h2 className="doctors-header-title">
            Find & Book the <span className="doctors-header-highlight">Best Doctors</span>
          </h2>
          <p className="doctors-header-subtitle">
            Book instant appointments with Pakistan's top therapists
          </p>
        </div>
      </section>

      {/* ===== DOCTORS SECTION ===== */}
      <section className="doctors-section">

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>
            Loading doctors...
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{ textAlign: "center", padding: "60px", color: "#c0392b" }}>
            {error}
          </div>
        )}

        {/* Doctors grid */}
        {!loading && !error && (
          <div className="doctors-grid">
            {doctors.slice(0, 3).map((doctor, i) => (
              <div
                key={doctor.id}
                className="doctor-card"
                ref={(el) => (cardsRef.current[i] = el)}
              >
                {/* Image */}
                <div className="doctor-image-container">
                  <img
                    src={
                      doctor.profile_image_url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&size=400&background=FF6F00&color=fff&bold=true`
                    }
                    alt={doctor.name}
                    className="doctor-image"
                  />
                  <div className="verified-badge">✓ Verified</div>
                  <div className="available-badge">{doctor.available}</div>
                </div>

                {/* Info */}
                <div className="doctor-info">
                  <h3 className="doctor-name">{doctor.name}</h3>
                  <p className="doctor-specialty">{doctor.specialization}</p>
                  <p className="doctor-degree">{doctor.education}</p>

                  <div className="doctor-meta">
                    <span>👤 {doctor.experience} years</span>
                    <span>⭐ {doctor.rating ?? "New"} ({doctor.reviews ?? 0})</span>
                  </div>

                  <div className="doctor-location">📍 {doctor.location}</div>

                  <div className="doctor-fee">
                    <span>Consultation Fee</span>
                    <span>Rs. {Number(doctor.fee).toLocaleString()}</span>
                  </div>

                  <div className="action-buttons">
                    <button className="btn-book"    onClick={() => handleBook(doctor)}>Book Now</button>
                    <button className="btn-profile" onClick={() => handleProfile(doctor)}>View Profile</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No doctors */}
        {!loading && !error && doctors.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>
            No approved doctors available right now.
          </div>
        )}

        <div className="view-all-container">
          <button className="view-all-button" onClick={() => navigate("/doctors")}>
            View All Therapists →
          </button>
        </div>
      </section>

      {showBooking && selectedDoctor && (
        <BookingPage doctor={selectedDoctor} onClose={handleClose} />
      )}
      {showProfile && selectedDoctor && (
        <DoctorProfile
          doctor={selectedDoctor}
          onClose={handleClose}
          onBookAppointment={() => handleBook(selectedDoctor)}
        />
      )}
    </>
  );
};

export default DoctorsSection;